import {Component} from 'preact';
import identity from 'ramda/src/identity';
import memoizeWith from 'ramda/src/memoizeWith';
import reduce from 'ramda/src/reduce';
import Observer from 'react-intersection-observer';
import {toCurrency} from '@util/NumberUtil';

const getHighestTier = memoizeWith( identity,
    reduce( ( prev, next ) => next.threshold > prev.threshold ? next : prev, {threshold: 0} ) );

const withObserver = ( Component ) => {
    function Wrapper ( props ) {
        return (<Observer className="inline-block">
            {inView => (<Component {...props} inView={inView}/>)}
        </Observer>)
    }

    Wrapper.displayName = `withObserver(${Component.displayName || Component.name})`;

    return Wrapper;
};

class _UEStatusMeter extends Component {

    static defaultProps = {
        size: 101,
        currentRank: null, // oneOf( ['Bronze', 'Silver', 'Gold', 'Platinum'] )
        nextRank: 'Bronze',
        currentYearSales: 0,
        amountAway: 0,
        tiers: [{threshold: 0}]
    };

    getMeterValue = () => {
        const {
            currentYearSales,
            tiers
        } = this.props;
        const {threshold} = getHighestTier( tiers );
        return (Math.max( Math.min( currentYearSales / (threshold), 1 ), 0 ) * 285) || 0; // max rotation 0 - 285deg
    };

    render ( {
                 amountAway,
                 currentRank,
                 nextRank,
                 currentYearSales,
                 className,
                 size,
                 showFullDetail = false
             } ) {
        const classList = className || '';
        const currentYearSalesFormatted = toCurrency( currentYearSales, true );
        const amountAwayFormatted = toCurrency( amountAway, true );
        const currentSalesTextSize = size < 150 ? 'text-base' : 'text-lg';

        const currentInfo = (function () {
            switch ( currentRank?.toLowerCase() ) {
                case 'bronze':
                    return <span
                        className="hidden eliteInfoText z-10 absolute bg-white p-2 text-left border border-black border-solid w-64 leading-normal text-base italic">You’ve earned <span
                        className="text-bronze">bronze-level</span> benefits this year! Keep going to earn <span
                        className="text-silver">silver level</span> for additional benefits.</span>;
                case 'silver':
                    return <span
                        className="hidden eliteInfoText z-10 absolute bg-white p-2 text-left border border-black border-solid w-64 leading-normal text-base italic">You’ve earned <span
                        className="text-silver">silver-level</span> benefits this year! Keep going to earn <span
                        className="text-gold">gold level</span> for additional benefits.</span>;
                case 'gold':
                    return <span
                        className="hidden eliteInfoText z-10 absolute bg-white p-2 text-left border border-black border-solid w-64 leading-normal text-base italic">You’ve earned <span
                        className="text-gold">gold-level</span> benefits this year! Keep going to earn <span
                        className="text-platinum">platinum level</span> for additional benefits.</span>;
                case 'platinum':
                    return <span
                        className="hidden eliteInfoText z-10 absolute bg-white p-2 text-left border border-black border-solid w-64 leading-normal text-base italic">You’ve earned <span
                        className="text-platinum">platinum level</span> benefits this year! Congrats you’ve unlocked all benefits.</span>;
                default:
                    return <span
                        className="hidden eliteInfoText z-10 absolute bg-white p-2 text-left border border-black border-solid w-64 leading-normal text-base italic">The <strong>Ultradent Elite</strong> program is our way of saying thanks and rewarding you with discounts, faster shipping, and other great benefits!</span>;
            }
        })();

        // Helper to render meter text for ranks except platinum/default
        const renderMeterText = ( amountAwayFormatted, nextRank, colorClass, currentInfo ) => (
            <span className="block text-center whitespace-normal">
                ${amountAwayFormatted}<br></br>to next level&nbsp;<span className={colorClass}>{nextRank}</span>
                <sup class="infoIcon relative inline-block">
                    <img aria-label="Current tier information" role="img" alt="Info icon"
                         src="https://images.ctfassets.net/wfptrcrbtkd0/4jlkT1R69v1H7KEyIifhro/ff5a3bd27d6a58f37a9d592427184ee8/info_icon_dark.svg"/>
                    {currentInfo}
                </sup>
            </span>);

        const meterText = (function () {
            const colorClass = `text-${nextRank?.toLowerCase()}`;
            switch ( currentRank?.toLowerCase() ) {
                case 'bronze':
                case 'silver':
                case 'gold':
                    return renderMeterText( amountAwayFormatted, nextRank, colorClass, currentInfo );
                case 'platinum':
                    return (<span className="block">
                            <span>Platinum Achieved!</span>
                            <sup class="infoIcon relative whitespace-normal">
                                <img aria-label="Current tier information" role="img" alt="Info icon"
                                     src="https://images.ctfassets.net/wfptrcrbtkd0/4jlkT1R69v1H7KEyIifhro/ff5a3bd27d6a58f37a9d592427184ee8/info_icon_dark.svg"/>
                                {currentInfo}
                            </sup>
                        </span>);
                default:
                    return renderMeterText( amountAwayFormatted, 'bronze', colorClass, currentInfo );
            }
        })();

        return (<div>
            <div data-testid="UEStatusMeter" ref={ref => this.meter = ref} className={`fade-in relative ${classList}`}>
                <svg data-testid="UEStatusMeter-svg" height={size} width={size} viewBox="0 0 104 104"
                     xmlns="http://www.w3.org/2000/svg"
                     preserveAspectRatio="xMidYMid meet"
                     className="mx-auto">
                    <g fill="none" fillRule="evenodd">
                        <path
                            d="M75.696 97.494a51.505 51.505 0 0 0 28.276-45.97C103.972 23.1 80.893.02 52.467.02 24.041.02.962 23.1.962 51.525a51.505 51.505 0 0 0 28.277 45.97l7.448-14.741a34.99 34.99 0 0 1-19.21-31.23c0-19.31 15.679-34.989 34.99-34.989 19.312 0 34.99 15.679 34.99 34.99a34.99 34.99 0 0 1-19.21 31.229l7.449 14.74z"
                            fill="#535353"/>
                        <path
                            d="M2.806 58.743a50.183 50.183 0 0 0 24.248 36.054l7.126-12.132a36.113 36.113 0 0 1-17.45-25.946L2.806 58.743z"
                            fill="#F6F5F4"/>
                        <path
                            d="M3.667 39.825a50.18 50.18 0 0 0-1.023 17.699l13.97-1.682a36.122 36.122 0 0 1 .736-12.736l-13.683-3.28z"
                            fill="#B46A1F"/>
                        <path
                            d="M48.613 1.49C27.337 3.13 9.425 18.043 3.959 38.669l13.6 3.605a36.112 36.112 0 0 1 32.134-26.755L48.613 1.49z"
                            fill="#A7A9AC"/>
                        <path
                            d="M87.043 87.895a50.182 50.182 0 0 0 15.607-36.37c0-27.697-22.486-50.183-50.183-50.183-.92 0-1.839.025-2.757.076l.773 14.049a36.12 36.12 0 0 1 1.984-.055c19.931 0 36.113 16.182 36.113 36.113 0 9.894-4.06 19.356-11.231 26.173l9.694 10.197z"
                            fill="#B4975A"/>
                        <path
                            d="M77.967 94.746A50.18 50.18 0 0 0 86.2 88.68L76.742 78.26a36.11 36.11 0 0 1-5.925 4.367l7.15 12.118z"
                            fill="#696765"/>

                        <g>
                            <path
                                className="origin-center t-duration-long t-transform t-delay-med ease-out-cubic"
                                style={`transform:rotate(${this.props.inView ? this.getMeterValue() : 0}deg)`}
                                stroke="#F8F8F8"
                                fill="#535353"
                                d="M26.48 90.104l5.453-15.671 6.645 4.315z"/>
                            <path
                                d="M76.296 32.501c-10.37-13.274-29.538-15.627-42.812-5.256-13.274 10.37-15.627 29.538-5.257 42.812C38.598 83.33 57.766 85.684 71.04 75.313c13.274-10.37 15.628-29.538 5.257-42.812zm.788-.615c10.71 13.709 8.28 33.505-5.429 44.215s-33.505 8.28-44.216-5.429c-10.71-13.709-8.28-33.505 5.43-44.215 13.708-10.711 33.504-8.28 44.215 5.429z"
                                fill="#8C8C8C"
                                fillRule="nonzero"/>
                        </g>
                    </g>
                </svg>
                <div className="text-center leading-normal absolute-center -mt-1">
                    <div className={`font-bold ${currentSalesTextSize}`}>${currentYearSalesFormatted}</div>
                </div>
            </div>
            {nextRank && <div id="next-rank" className="text-sm leading-tight mt-1">
                {meterText}
            </div>}
            </div>);
    }
}

export const UEStatusMeter = _UEStatusMeter;

export default withObserver( _UEStatusMeter );